import React from 'react';
import { Paper, Divider, IconButton, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { X, Plus, Clock } from 'lucide-react';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { TemplatePreview } from './TemplatePreview';
import { ConversationList } from './ConversationList';
import { useMessageFeedback, type SubmitFeedbackFn } from './useMessageFeedback';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    width: 820,
    maxWidth: '95vw',
    height: 600,
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    zIndex: theme.zIndex.modal + 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  body: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  chatPane: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  previewPane: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
}));

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: Date;
}

interface ConversationSummary {
  id: string;
  title: string | null;
  messageCount: number;
  lastMessageAt: string;
  createdAt: string;
}

interface ExperienceBuilderViewProps {
  messages: Message[];
  isLoading: boolean;
  onSend: (message: string) => void;
  onClose: () => void;
  onNewChat: () => void;
  template: unknown | null;
  isTemplateLoading: boolean;
  // History props
  conversations: ConversationSummary[];
  isLoadingHistory: boolean;
  activeConversationId: string | null;
  onLoadConversation: (id: string) => void;
  submitFeedback?: SubmitFeedbackFn;
}

export const ExperienceBuilderView: React.FC<ExperienceBuilderViewProps> = ({
  messages,
  isLoading,
  onSend,
  onClose,
  onNewChat,
  template,
  isTemplateLoading,
  conversations,
  isLoadingHistory,
  activeConversationId,
  onLoadConversation,
  submitFeedback,
}) => {
  const classes = useStyles();
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const { feedbackByMsg, handleRate, handleToggleComment, handleSubmitComment, reset } =
    useMessageFeedback(submitFeedback);

  React.useEffect(() => {
    reset();
  }, [activeConversationId, reset]);
  const [showHistory, setShowHistory] = React.useState(false);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <Paper elevation={8} className={classes.root}>
      <div className={classes.header}>
        <Typography variant="subtitle1">Experience Builder</Typography>
        <div>
          <IconButton
            size="small"
            onClick={() => setShowHistory(!showHistory)}
            style={{ color: 'inherit' }}
            title="Conversation history"
          >
            <Clock size={18} />
          </IconButton>
          <IconButton
            size="small"
            onClick={onNewChat}
            style={{ color: 'inherit' }}
            title="New conversation"
          >
            <Plus size={18} />
          </IconButton>
          <IconButton
            size="small"
            onClick={onClose}
            style={{ color: 'inherit' }}
          >
            <X size={18} />
          </IconButton>
        </div>
      </div>

      <div className={classes.body}>
        {/* Left: Chat */}
        <div className={classes.chatPane}>
          {showHistory ? (
            <ConversationList
              conversations={conversations}
              isLoading={isLoadingHistory}
              activeConversationId={activeConversationId}
              onSelect={(id) => {
                onLoadConversation(id);
                setShowHistory(false);
              }}
              onBack={() => setShowHistory(false)}
            />
          ) : (
            <>
              <div className={classes.messages}>
                {messages.map((msg, idx) => (
                  <ChatMessage
                    key={msg.id}
                    role={msg.role}
                    content={msg.content}
                    showFeedback={msg.role === 'assistant'}
                    feedback={feedbackByMsg[msg.id]}
                    onRate={(rating) => handleRate(msg.id, idx, rating, msg.content)}
                    onToggleComment={() => handleToggleComment(msg.id)}
                    onSubmitComment={(text) => handleSubmitComment(msg.id, idx, text, msg.content)}
                  />
                ))}
                {isLoading && <ChatMessage role="assistant" content="" isLoading />}
                <div ref={messagesEndRef} />
              </div>
              <Divider />
              <ChatInput onSend={onSend} disabled={isLoading} />
            </>
          )}
        </div>

        {/* Right: Template Preview */}
        <div className={classes.previewPane}>
          <TemplatePreview
            template={template as any}
            isLoading={isTemplateLoading}
          />
        </div>
      </div>
    </Paper>
  );
};
